import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { ComparedVideo, ComparisonMetric, RetentionData, formatMetricValue, VIDEO_COLORS } from './videoDuelData';
import { buildPitchPositionRow } from './pitchPositionRow';
import { PitchSelector } from '@/components/metrics/pitch/PitchSelector';
import { PitchQuickModal } from '@/components/metrics/pitch/PitchQuickModal';
import { byPositionOptions, resolvePitchSelection } from '@/components/metrics/pitch/pitchSelection';
import { toVideoPitches } from '@/utils/pitches';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SlidersHorizontal, ArrowUpDown, ChevronUp, ChevronDown, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

interface Props {
  metrics: ComparisonMetric[];
  videos: ComparedVideo[];
  /** `retention.pitchesA/B/C`: a linha "Retenção no Pitch" é montada por posição. */
  retention?: RetentionData | null;
  /** Posição escolhida no seletor ("0" = 1º pitch). Fica no pai para sobreviver ao refetch. */
  pitchSelection: string | null;
  onPitchSelectionChange: (value: string) => void;
  /** Refaz a comparação depois de definir um pitch pela tabela. */
  onPitchSaved?: () => void;
}

const PITCH_ROW_KEY = 'pitch_retention';

const ALL_METRIC_KEYS = [
  { key: 'impressions', get label() { return translateInterfaceText("Visualizações"); } },
  { key: 'impressions_unique', get label() { return translateInterfaceText("Visualizações Únicas"); } },
  { key: 'plays', get label() { return translateInterfaceText("Plays"); } },
  { key: 'plays_unique', get label() { return translateInterfaceText("Plays Únicos"); } },
  { key: 'play_rate', get label() { return translateInterfaceText("Play Rate"); } },
  { key: 'avg_retention', get label() { return translateInterfaceText("Retenção Média"); } },
  { key: 'pitch_retention', get label() { return translateInterfaceText("Retenção no Pitch"); } },
  { key: 'button_clicks', get label() { return translateInterfaceText("Cliques no Botão"); } },
  { key: 'conversions', get label() { return translateInterfaceText("Conversões"); } },
  { key: 'revenue', get label() { return translateInterfaceText("Receita"); } },
];

type SortDir = 'asc' | 'desc' | null;
type SortCol = 'A' | 'B' | 'C' | 'diff' | null;

const numericBase = "font-[family-name:'Inter',system-ui,sans-serif] tabular-nums lining-nums tracking-[-0.01em] leading-tight";

function renderNumber(value: number, isWinner: boolean) {
  const formatted = Math.round(value).toLocaleString(getInterfaceLanguage());
  return (
    <span className={`${numericBase} text-[15px] font-semibold ${isWinner ? 'text-[#4B7BFF]' : 'text-[#1A1F36]'}`}>
      {formatted}
    </span>
  );
}

function renderPercent(value: number, isWinner: boolean) {
  const num = value.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <span className={`${numericBase} text-[15px] font-bold ${isWinner ? 'text-[#4B7BFF]' : 'text-[#1A1F36]'}`}>
      {num}
      <span className="text-[13px] font-semibold">%</span>
    </span>
  );
}

function renderCurrency(value: number, isWinner: boolean) {
  const parts = new Intl.NumberFormat(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).formatToParts(value);
  const intPart = parts.filter(part => ['integer', 'group', 'minusSign'].includes(part.type)).map(part => part.value).join('');
  const decPart = parts.find(part => part.type === 'fraction')?.value ?? '00';
  const decimal = parts.find(part => part.type === 'decimal')?.value ?? '.';
  return (
    <span className={`${numericBase} text-[16px] font-bold ${isWinner ? 'text-[#4B7BFF]' : 'text-[#1A1F36]'}`}>
      <span className="text-[13px] font-semibold mr-[2px]">R$</span>
      {intPart}
      <span className="text-[13px] font-semibold">{decimal}{decPart}</span>
    </span>
  );
}

function renderDifference(label: string) {
  if (label === 'Empate' || label === '—') {
    return <span className={`${numericBase} text-[13px] font-medium text-[hsl(var(--muted-foreground))]`}>{translateInterfaceText(label)}</span>;
  }
  return <span className={`${numericBase} text-[14px] font-semibold text-[#2563EB]`}>{translateInterfaceText(label)}</span>;
}

function renderFormattedValue(value: number, format: ComparisonMetric['format'], isWinner: boolean) {
  if (format === 'percent') return renderPercent(value, isWinner);
  if (format === 'currency') return renderCurrency(value, isWinner);
  return renderNumber(value, isWinner);
}

export function ComparisonDataTable({
  metrics,
  videos,
  retention,
  pitchSelection,
  onPitchSelectionChange,
  onPitchSaved,
}: Props) {
  const interfaceLanguage = useLanguage();
  const letters = ['A', 'B', 'C'].slice(0, videos.length) as ('A' | 'B' | 'C')[];

  // Linha "Retenção no Pitch" por posição: cada vídeo usa o pitch da posição escolhida.
  const pitchLists = useMemo(
    () => [retention?.pitchesA, retention?.pitchesB, retention?.pitchesC].slice(0, videos.length),
    [retention, videos.length],
  );
  const pitchOptions = useMemo(() => byPositionOptions(pitchLists), [pitchLists]);
  const pitchPosition = Number(resolvePitchSelection(pitchOptions, pitchSelection) ?? 0);
  const pitchRow = useMemo(() => buildPitchPositionRow(pitchLists, pitchPosition), [pitchLists, pitchPosition, interfaceLanguage]);
  // Vídeo (índice) em que o usuário clicou "Definir"; null = modal fechado.
  const [definingIndex, setDefiningIndex] = useState<number | null>(null);

  // A linha de pitch do backend (1º pitch) é trocada pelos valores da posição escolhida.
  const displayMetrics = useMemo(
    () =>
      metrics.map((m) =>
        m.key !== PITCH_ROW_KEY || !retention
          ? m
          : {
              ...m,
              videoAValue: pitchRow.cells[0]?.value ?? 0,
              videoBValue: pitchRow.cells[1]?.value ?? 0,
              videoCValue: m.videoCValue !== undefined ? pitchRow.cells[2]?.value ?? 0 : undefined,
              winner: pitchRow.winner,
              differenceLabel: pitchRow.differenceLabel,
            },
      ),
    [metrics, retention, pitchRow],
  );
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set(ALL_METRIC_KEYS.map((m) => m.key)));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [sortCol, setSortCol] = useState<SortCol>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const toggleKey = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setVisibleKeys(new Set(ALL_METRIC_KEYS.map((m) => m.key)));
  const selectNone = () => setVisibleKeys(new Set());

  const handleSort = (col: SortCol) => {
    if (sortCol === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') {
        setSortCol(null);
        setSortDir(null);
      } else setSortDir('asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortedMetrics = useMemo(() => {
    if (!sortCol || !sortDir) return displayMetrics;
    return [...displayMetrics].sort((a, b) => {
      let valA: number, valB: number;
      if (sortCol === 'A') {
        valA = a.videoAValue;
        valB = b.videoAValue;
      } else if (sortCol === 'B') {
        valA = a.videoBValue;
        valB = b.videoBValue;
      } else if (sortCol === 'C') {
        valA = a.videoCValue ?? 0;
        valB = b.videoCValue ?? 0;
      } else {
        valA = Math.abs(a.videoAValue - a.videoBValue);
        valB = Math.abs(b.videoAValue - b.videoBValue);
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
  }, [displayMetrics, sortCol, sortDir]);

  const SortIcon = ({ col }: { col: SortCol }) => {
  useLanguage();
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />;
    return sortDir === 'asc' ? (
      <ChevronUp className="h-3 w-3 ml-1 inline text-[hsl(var(--primary))]" />
    ) : (
      <ChevronDown className="h-3 w-3 ml-1 inline text-[hsl(var(--primary))]" />
    );
  };

  const buildRows = useCallback(() => {
    const headers = ['Métrica', ...videos.map((v) => v.name), 'Diferença', 'Melhor'];
    const rows = sortedMetrics.map((m) => {
      const vals = [m.videoAValue, m.videoBValue];
      if (m.videoCValue !== undefined) vals.push(m.videoCValue);
      const isPitchRow = m.key === PITCH_ROW_KEY && !!retention;
      const cells = vals.map((v, i) =>
        isPitchRow && pitchRow.cells[i]?.value == null ? '—' : formatMetricValue(v, m.format),
      );
      return [translateInterfaceText(m.label), ...cells, translateInterfaceText(m.differenceLabel), m.winner === 'tie' ? '=' : m.winner];
    });
    return { headers, rows };
  }, [sortedMetrics, videos, retention, pitchRow]);

  const exportCsv = useCallback(() => {
    const { headers, rows } = buildRows();
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'comparacao-vsl.csv';
    a.click();
    URL.revokeObjectURL(url);
    setExportOpen(false);
    toast.success(translateInterfaceText("CSV exportado com sucesso"));
  }, [buildRows]);

  const exportPdf = useCallback(() => {
    const { headers, rows } = buildRows();
    const printWin = window.open('', '_blank');
    if (!printWin) {
      toast.error(translateInterfaceText("Popup bloqueado. Permita popups para exportar."));
      return;
    }

    const thStyle =
      'padding:8px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;border-bottom:2px solid #e5e7eb;background:#f9fafb;';
    const tdStyle = 'padding:8px 12px;font-size:13px;border-bottom:1px solid #f3f4f6;';

    const tableHtml = `
      <table style="width:100%;border-collapse:collapse;font-family:system-ui,-apple-system,sans-serif;">
        <thead><tr>${headers.map((h) => `<th style="${thStyle}">${h}</th>`).join('')}</tr></thead>
        <tbody>${rows
          .map(
            (r, i) =>
              `<tr style="background:${i % 2 === 0 ? '#fafbfc' : '#fff'}">${r
                .map((c) => `<td style="${tdStyle}">${c}</td>`)
                .join('')}</tr>`,
          )
          .join('')}</tbody>
      </table>`;

    printWin.document.write(`<!DOCTYPE html><html><head><title>Comparação VSL</title><style>@media print{body{margin:0}}</style></head><body style="padding:24px">
      <h1 style="font-family:system-ui;font-size:18px;margin-bottom:4px">Tabela Consolidada</h1>
      <p style="font-family:system-ui;font-size:12px;color:#6b7280;margin-bottom:16px">${new Date().toLocaleDateString(getInterfaceLanguage())}</p>
      ${tableHtml}
    </body></html>`);
    printWin.document.close();
    setTimeout(() => {
      printWin.print();
    }, 300);
    setExportOpen(false);
    toast.success(translateInterfaceText("PDF pronto para impressão"));
  }, [buildRows]);

  return (
    <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">{translateInterfaceText("Tabela Consolidada")}</h3>

        <div className="flex items-center gap-2">
          {visibleKeys.has(PITCH_ROW_KEY) && (
            <PitchSelector options={pitchOptions} value={String(pitchPosition)} onChange={onPitchSelectionChange} />
          )}
          <div className="relative">
            <button
              onClick={() => setExportOpen(!exportOpen)}
              className="flex items-center gap-2 text-[12px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors px-3 py-1.5 rounded-lg border border-[hsl(var(--border))]"
            >
              <Download className="h-3.5 w-3.5" />
              {translateInterfaceText("Exportar")}</button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 top-full mt-2 z-50 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] p-1.5 min-w-[160px]">
                  <button
                    onClick={exportCsv}
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[12px] font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors"
                  >
                    <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                    {translateInterfaceText("Exportar CSV")}</button>
                  <button
                    onClick={exportPdf}
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[12px] font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors"
                  >
                    <FileText className="h-4 w-4 text-red-500" />
                    {translateInterfaceText("Exportar PDF")}</button>
                </div>
              </>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setPopoverOpen(!popoverOpen)}
              className="flex items-center gap-2 text-[12px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors px-3 py-1.5 rounded-lg border border-[hsl(var(--border))]"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {translateInterfaceText("Colunas (")}{visibleKeys.size}/{ALL_METRIC_KEYS.length})
            </button>
            {popoverOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setPopoverOpen(false)} />
                <div
                  ref={popoverRef}
                  className="absolute right-0 top-full mt-2 z-50 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] p-3 min-w-[220px]"
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[12px] font-semibold text-[hsl(var(--foreground))]">{translateInterfaceText("Colunas")}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={selectAll} className="text-[11px] font-medium text-[hsl(var(--primary))] hover:underline">
                        {translateInterfaceText("Todas")}</button>
                      <button onClick={selectNone} className="text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:underline">
                        {translateInterfaceText("Desmarcar todas")}</button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {ALL_METRIC_KEYS.map((m) => (
                      <label
                        key={m.key}
                        className="flex items-center gap-2.5 py-1.5 px-1 rounded-md hover:bg-[hsl(var(--muted))]/50 cursor-pointer transition-colors"
                      >
                        <Checkbox checked={visibleKeys.has(m.key)} onCheckedChange={() => toggleKey(m.key)} className="h-4 w-4" />
                        <span className="text-[12px] text-[hsl(var(--foreground))]">{translateInterfaceText(m.label)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-[hsl(var(--border))]">
              <TableHead className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-[0.06em] w-[200px] py-3.5">
                {translateInterfaceText("Métrica")}</TableHead>
              {videos.map((v, i) => (
                <TableHead
                  key={v.id}
                  className="text-[11px] font-bold text-center uppercase tracking-[0.06em] cursor-pointer select-none hover:opacity-80 py-3.5"
                  style={{ color: VIDEO_COLORS[i] }}
                  onClick={() => handleSort(letters[i])}
                >
                  {v.name}
                  <SortIcon col={letters[i]} />
                </TableHead>
              ))}
              <TableHead
                className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] text-center uppercase tracking-[0.06em] cursor-pointer select-none hover:opacity-80 py-3.5"
                onClick={() => handleSort('diff')}
                title={translateInterfaceText("Mostra quanto o vídeo vencedor está à frente do segundo colocado nesta métrica.")}
              >
                {translateInterfaceText("Diferença")}<SortIcon col="diff" />
              </TableHead>
              <TableHead className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] text-center uppercase tracking-[0.06em] py-3.5">
                {translateInterfaceText("Melhor")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedMetrics.map((m, i) => {
              const values = [m.videoAValue, m.videoBValue];
              if (m.videoCValue !== undefined) values.push(m.videoCValue);
              const isVisible = visibleKeys.has(m.key);
              const isRevenueRow = m.key === 'revenue';

              return (
                <TableRow key={m.key} className={`${i % 2 === 0 ? 'bg-[hsl(var(--muted))/0.08]' : ''} ${isRevenueRow ? 'bg-[hsl(var(--muted))/0.12]' : ''}`}>
                  <TableCell className="text-[13px] font-medium text-[hsl(var(--muted-foreground))] py-3.5">{translateInterfaceText(m.label)}</TableCell>
                  {values.map((val, vi) => {
                    const pitchCell = m.key === PITCH_ROW_KEY && retention ? pitchRow.cells[vi] : undefined;
                    return (
                      <TableCell key={vi} className="text-center py-3.5 px-4">
                        {!isVisible ? (
                          <span className="text-[hsl(var(--muted-foreground))]">—</span>
                        ) : pitchCell === undefined ? (
                          renderFormattedValue(val, m.format, m.winner === letters[vi])
                        ) : pitchCell.pitch ? (
                          <div className="flex flex-col items-center gap-0.5">
                            {pitchCell.value != null ? (
                              renderPercent(pitchCell.value, m.winner === letters[vi])
                            ) : (
                              <span className="text-[hsl(var(--muted-foreground))]">—</span>
                            )}
                            {pitchOptions.length >= 2 && (
                              <span
                                className="max-w-[140px] truncate text-[11px] text-[hsl(var(--muted-foreground))]"
                                title={pitchCell.label ?? undefined}
                              >
                                {pitchCell.label}
                              </span>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDefiningIndex(vi)}
                            title={translateInterfaceText("Vídeo sem pitch nesta posição — clique para definir")}
                            className="text-[13px] font-medium text-primary hover:underline underline-offset-2"
                          >
                            {translateInterfaceText("Definir")}</button>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center py-3.5 px-4">
                    {isVisible ? renderDifference(m.differenceLabel) : <span className="text-[hsl(var(--muted-foreground))]">—</span>}
                  </TableCell>
                  <TableCell className="text-center py-3.5">
                    {isVisible ? (
                      m.winner !== 'tie' ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 text-[11px] font-bold tracking-wide rounded-full bg-gradient-to-b from-[#EEF2FF] to-[#E0E7FF] text-[#4B7BFF] shadow-[0_1px_3px_rgba(75,123,255,0.2)] border border-[#D9E2FF]/60">
                          {m.winner}
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-7 h-7 text-[10px] font-semibold rounded-full bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))]">
                          =
                        </span>
                      )
                    ) : (
                      <span className="text-[hsl(var(--muted-foreground))]">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {definingIndex !== null && videos[definingIndex] && (
        <PitchQuickModal
          isOpen
          onClose={() => setDefiningIndex(null)}
          videoId={videos[definingIndex].id}
          pitches={toVideoPitches(pitchLists[definingIndex])}
          editingPitchId={null}
          durationInSeconds={videos[definingIndex].durationInSeconds}
          onSaved={() => onPitchSaved?.()}
        />
      )}
    </div>
  );
}
